import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: object;
}

const SEO = ({
  title = "Shophentic - Authentic Lifestyle & Tech Product Reviews",
  description = "Discover authentic product reviews for lifestyle and tech products. Expert buying guides, honest recommendations, and trusted shopping links from Amazon and top marketplaces.",
  keywords = "product reviews, lifestyle products, tech reviews, buying guides, Amazon affiliate, honest reviews, shopping recommendations",
  image = "https://shophentic.bishalmondal.online/hero-tech-reviews.jpg",
  url = "https://shophentic.bishalmondal.online",
  type = "website",
  author = "Bishal Mondal",
  publishedTime,
  modifiedTime,
  structuredData
}: SEOProps) => {
  const fullTitle = title.includes("Shophentic") ? title : `${title} | Shophentic`;
  
  useEffect(() => {
    // Update page title for accessibility
    document.title = fullTitle;
  }, [fullTitle]);

  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": type === "article" ? "Article" : "WebPage",
    "name": fullTitle,
    "description": description,
    "url": url,
    "image": image,
    "author": {
      "@type": "Person",
      "name": author,
      "url": "https://bishalmondal.online"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Shophentic",
      "url": "https://shophentic.bishalmondal.online",
      "logo": {
        "@type": "ImageObject",
        "url": "https://shophentic.bishalmondal.online/logo.png"
      }
    },
    ...(publishedTime && { "datePublished": publishedTime }),
    ...(modifiedTime && { "dateModified": modifiedTime }),
    ...(type === "article" && {
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": url
      }
    })
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Shophentic" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@bishalmondalcom" />
      <meta name="twitter:creator" content="@bishalmondalcom" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO; 